import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Banner, BlockStack, Button, DataTable, InlineStack, ProgressBar, Select, Text } from '@shopify/polaris';
import axios from 'axios';
import agent from '../../app/api/agent';
import { LegacyStoreRefreshJob, LegacyStoreRefreshRun } from '../../app/models/Admin';
import { formatLocalDateTime } from './utils/dateTime';

type Props = {
  compact?: boolean;
  token: string;
  onUnauthorized: () => void;
  onProgress?: () => void;
  onActiveChange?: (active: boolean) => void;
  inventoryReport?: LegacyStoreRefreshRun;
  onReloadList?: () => void;
  listLoading?: boolean;
};
export const isRefreshActive = (job?: LegacyStoreRefreshJob | null) => !!job && ['Queued', 'Running'].includes(job.status);

const warningList = (json?: string): string[] => {
  try { const value = JSON.parse(json || '[]'); return Array.isArray(value) ? value.filter(x => typeof x === 'string') : []; }
  catch { return ['The stored diagnostic warning report could not be read.']; }
};

export default function LegacyInventoryRefreshPanel({ token, onUnauthorized, onProgress, onActiveChange, compact,
  inventoryReport, onReloadList, listLoading }: Props) {
  const [jobs, setJobs] = useState<LegacyStoreRefreshJob[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<LegacyStoreRefreshJob | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const progressCallback = useRef(onProgress);
  const previousSnapshot = useRef<string | null>(null);
  const requestVersion = useRef(0);
  progressCallback.current = onProgress;
  const fail = useCallback((e: unknown) => {
    if (axios.isAxiosError(e) && e.response?.status === 401) onUnauthorized();
    else setError(axios.isAxiosError(e) && typeof e.response?.data?.message === 'string'
      ? e.response.data.message : 'Unable to read or update the background refresh. Its last known status is shown; the worker may still be running.');
  }, [onUnauthorized]);
  const load = useCallback(async () => {
    const version = ++requestVersion.current;
    try {
      const result = await agent.Admin.legacyRefreshJobs(token);
      if (version !== requestVersion.current) return;
      setJobs(result); setLoaded(true); setError(null);
      const snapshot = JSON.stringify(result.map(x => [x.id, x.status, x.updatedAt, x.completedStores, x.failedStores]));
      if (previousSnapshot.current !== null && snapshot !== previousSnapshot.current) progressCallback.current?.();
      previousSnapshot.current = snapshot;
      const selected = result.find(x => x.id === selectedId) ?? result[0];
      if (selected) {
        const updated = await agent.Admin.legacyRefreshJob(token, selected.id);
        if (version === requestVersion.current) setDetail(updated);
      }
      else setDetail(null);
    } catch (e) { if (version === requestVersion.current) fail(e); }
  }, [token, selectedId, fail]);
  useEffect(() => { void load(); return () => { requestVersion.current++; }; }, [load]);
  const active = jobs.some(isRefreshActive) || isRefreshActive(detail);
  useEffect(() => { onActiveChange?.(!loaded || active); }, [loaded, active, onActiveChange]);
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(timer);
  }, [active, load]);
  const act = async (kind: 'queue' | 'cancel' | 'retry') => {
    if (busy || !loaded || (kind === 'queue' && active) || (kind !== 'queue' && !detail)) return;
    if (kind === 'cancel' && (!isRefreshActive(detail) || detail!.cancellationRequested)) return;
    if (kind === 'retry' && active) return;
    const message = kind === 'queue'
      ? 'Queue a background PFV1 refresh for all source stores, including live subscription/access checks, read-only Shopify plan checks for store environment, and SQL preparation diagnostics? Results are saved in PFV2. This does not modify PFV1, Shopify, storefront routing, sync settings, or active garages.'
      : kind === 'cancel'
        ? 'Request cancellation? Completed inventory updates and check results remain saved. The worker stops at its next safe checkpoint.'
        : diagnosticsOnlyRetry ? 'Queue a retry of the SQL preparation diagnostics? Successful subscription and API-access results are retained. No migration or routing change will start.'
        : 'Queue a retry of failed or unfinished checks? Successful checks are retained. If inventory could not finish, the inventory phase runs again. No migration or routing change will start.';
    if (!window.confirm(message)) return;
    setBusy(true); setError(null);
    try {
      const result = kind === 'queue' ? await agent.Admin.refreshLegacyStores(token)
        : kind === 'cancel' ? await agent.Admin.cancelLegacyRefreshJob(token, detail!.id)
          : await agent.Admin.retryLegacyRefreshJob(token, detail!.id);
      requestVersion.current++;
      setJobs(current => [result, ...current.filter(x => x.id !== result.id)].slice(0, 20));
      setSelectedId(result.id); setDetail(result);
    } catch (e) { fail(e); }
    finally { setBusy(false); }
  };
  const finished = detail ? detail.completedStores + detail.failedStores + detail.skippedStores : 0;
  const warnings = warningList(detail?.diagnosticsWarningsJson);
  const diagnosticsOnlyRetry = !!detail && detail.failedStores === 0 && detail.pendingStores === 0 && warnings.length > 0;
  const canRetry = detail && !isRefreshActive(detail) && !active
    && (detail.failedStores > 0 || detail.pendingStores > 0 || detail.status === 'Failed'
      || (detail.status === 'Cancelled' && !detail.inventoryRunId) || diagnosticsOnlyRetry);
  return <BlockStack gap="300">
    <InlineStack align="space-between" blockAlign="center">
      <Text as="h2" variant={compact ? 'headingMd' : 'headingLg'}>{compact ? 'PFV1 inventory refresh' : 'Stores & migration'}</Text>
      <InlineStack gap="200"><Button onClick={() => { void load(); onReloadList?.(); }} loading={listLoading} disabled={busy}>{compact ? 'Reload refresh status' : 'Reload'}</Button>
        <Button variant="primary" onClick={() => void act('queue')} loading={busy} disabled={!loaded || active}>Refresh from PFV1</Button></InlineStack>
    </InlineStack>
    <Text as="p" tone="subdued">Refresh store records, mappings, subscriptions, API access and store environment. No storefront changes.</Text>
    {error ? <Banner tone="warning">{error}</Banner> : null}
    {!loaded && !error ? <Text as="p">Loading background refresh status…</Text> : null}
    {detail ? <>
      <InlineStack gap="300" blockAlign="center"><Badge tone={detail.status === 'Failed' || detail.status === 'CompletedWithIssues' ? 'warning' : 'info'}>
        {detail.cancellationRequested && isRefreshActive(detail) ? 'Cancellation requested'
          : detail.status === 'CompletedWithIssues' ? 'Completed with issues' : detail.status}</Badge>
        <Text as="p">{detail.totalStores > 0 ? `${finished} of ${detail.totalStores} store checks finished`
          : detail.status === 'Queued' ? 'Waiting for worker' : `Phase: ${detail.phase}`}</Text>
        <Text as="span" tone="subdued">{isRefreshActive(detail) ? 'Updates automatically' : formatLocalDateTime(detail.completedAt || detail.updatedAt)}</Text>
      </InlineStack>
      {isRefreshActive(detail) && detail.totalStores > 0 ? <div aria-label="PFV1 store checks finished"><ProgressBar progress={Math.min(100, finished / detail.totalStores * 100)} size="small" /></div> : null}
      {detail.errorCode || warnings.length > 0 || detail.failedStores > 0 ? <Banner tone="warning">
        Refresh needs attention. {detail.failedStores > 0 ? `${detail.failedStores} failed store checks. ` : ''}Review Refresh details below.
      </Banner> : null}
      <InlineStack gap="200">
        {isRefreshActive(detail) ? <Button onClick={() => void act('cancel')} disabled={busy || detail.cancellationRequested}>Cancel refresh</Button> : null}
        {canRetry ? <Button onClick={() => void act('retry')} disabled={busy}>{diagnosticsOnlyRetry ? 'Retry SQL diagnostics' : 'Retry failed / unfinished checks'}</Button> : null}
      </InlineStack>
    </> : inventoryReport ? <Text as="p" tone="subdued">
      Last inventory update: {formatLocalDateTime(inventoryReport.completedAt)} · {inventoryReport.sourceCount.toLocaleString()} stores
    </Text> : loaded ? <Text as="p" tone="subdued">No refresh runs yet.</Text> : null}
    {inventoryReport && (inventoryReport.conflicts > 0 || inventoryReport.missingFromSource > 0) ? <Banner tone="warning">
      {inventoryReport.conflicts} mapping issues · {inventoryReport.missingFromSource} stores missing from PFV1. Review the store list before migration.
    </Banner> : null}
    <details>
      <summary style={{ cursor: 'pointer', width: 'fit-content' }}>Refresh details</summary>
      <div style={{ paddingTop: 'var(--p-space-300)' }}><BlockStack gap="300">
        <Text as="p" tone="subdued">Runs inventory, exact-domain mappings, subscription and API-access checks, read-only Shopify plan checks for store environment, and SQL preparation diagnostics in the background. You can leave this page; unfinished work resumes after a service restart. No backfill, Shopify changes, sync activation, or routing switch is performed.</Text>
        <Text as="p" tone="subdued">Database status is not live billing verification. Subscription checks use each PFV1 app token; PFV2 subscriptions are N/A. A failed access check does not mean a store is unsubscribed.</Text>
        {inventoryReport ? <BlockStack gap="100">
          <Text as="h3" variant="headingSm">Last inventory update</Text>
          <Text as="p">{formatLocalDateTime(inventoryReport.completedAt)} by {inventoryReport.requestedBy} · {inventoryReport.sourceCount.toLocaleString()} source stores</Text>
          <Text as="p" tone="subdued">{inventoryReport.added} added · {inventoryReport.updated} refreshed · {inventoryReport.mapped} new links · {inventoryReport.sourceOnly} PFV1-only · {inventoryReport.conflicts} mapping issues · {inventoryReport.missingFromSource} missing from source</Text>
        </BlockStack> : null}
        {jobs.length > 1 ? <Select label="Refresh run" value={detail?.id || jobs[0].id} onChange={setSelectedId}
          options={jobs.map(x => ({ label: `${formatLocalDateTime(x.requestedAt)} · ${x.status}`, value: x.id }))} /> : null}
        {detail ? <>
          <Text as="p">Phase: {detail.phase} · {detail.completedStores} successful · {detail.failedStores} failed · {detail.skippedStores} skipped · {detail.pendingStores} pending</Text>
          <Text as="p" tone="subdued">Requested {formatLocalDateTime(detail.requestedAt)} by {detail.requestedBy}. Last worker heartbeat: {formatLocalDateTime(detail.heartbeatAt)}. {detail.completedAt ? `Finished ${formatLocalDateTime(detail.completedAt)}.` : 'Status refreshes every 5 seconds while active.'}</Text>
          {detail.status === 'Queued' ? <Text as="p">Waiting for the refresh worker. If this persists, check that ShopifySyncService is running and can access the refresh-job tables and PFV1 database.</Text> : null}
          {detail.errorCode ? <Text as="p" tone="caution">Refresh issue: {detail.errorCode}. Completed results are retained.</Text> : null}
          {warnings.map((warning, i) => <Text key={i} as="p" tone="caution">{warning}</Text>)}
          {detail.cancellationRequested && isRefreshActive(detail) ? <Text as="p">Cancellation is pending the worker’s next safe checkpoint. Saved results are not rolled back.</Text> : null}
          {(detail.issues?.length || 0) > 0 ? <>
            <Text as="h3" variant="headingSm">Store checks needing attention</Text>
            <DataTable columnContentTypes={['text', 'text', 'text', 'text']} headings={['PFV1 store', 'Status', 'Issue', 'Checked']}
              rows={detail.issues!.map(x => [`#${x.sourceShopId}`, x.status, x.errorCode || 'Unknown check failure', formatLocalDateTime(x.checkedAt)])} />
            {detail.failedStores > detail.issues!.length ? <Text as="p" tone="subdued">Showing the first {detail.issues!.length} failed checks. Open each store’s Legacy data for its latest saved report.</Text> : null}
          </> : null}
        </> : loaded ? <Text as="p" tone="subdued">No background refresh runs yet.</Text> : null}
      </BlockStack></div>
    </details>
  </BlockStack>;
}
